import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type ReviewerRole = 'APPLICATION_OWNER' | 'ARCHITECT';

type ReviewerDescriptor = {
  entityRef: string;
  displayName: string;
  roles: ReviewerRole[];
};

const evaluationIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{2,127}$/;

function isReviewersResponse(
  value: unknown,
): value is { evaluationId: string; reviewers: ReviewerDescriptor[] } {
  if (!value || typeof value !== 'object') return false;
  const response = value as {
    evaluationId?: unknown;
    reviewers?: unknown;
  };
  return (
    typeof response.evaluationId === 'string' &&
    Array.isArray(response.reviewers) &&
    response.reviewers.every(reviewer => {
      if (!reviewer || typeof reviewer !== 'object') return false;
      const candidate = reviewer as Partial<ReviewerDescriptor>;
      return (
        typeof candidate.entityRef === 'string' &&
        candidate.entityRef.startsWith('user:') &&
        typeof candidate.displayName === 'string' &&
        candidate.displayName.length > 0 &&
        Array.isArray(candidate.roles) &&
        candidate.roles.length > 0 &&
        candidate.roles.every(
          role => role === 'APPLICATION_OWNER' || role === 'ARCHITECT',
        )
      );
    })
  );
}

async function readJson(response: Response): Promise<unknown> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      // Keep the status-only message when an intermediary returns non-JSON.
    }
    throw new Error(message);
  }
  return response.json();
}

function roleLabel(role: ReviewerRole) {
  return role === 'APPLICATION_OWNER' ? 'Application owner' : 'Cloud architect';
}

export function HorizonReviewerPicker(
  props: FieldExtensionComponentProps<string>,
) {
  const { formData, onChange, rawErrors, required, disabled, readonly } = props;
  const rawEvaluationId = props.formContext?.formData?.evaluationId;
  const evaluationId =
    typeof rawEvaluationId === 'string' ? rawEvaluationId.trim() : '';
  const validEvaluationId = evaluationIdPattern.test(evaluationId);
  const fieldId = useId();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const requestSequence = useRef(0);
  const previousEvaluationId = useRef(evaluationId);
  const pendingSelection = useRef(formData);
  const initialSelectionHandled = useRef(false);
  const selectedValue = useRef(formData);
  const changeSelection = useRef(onChange);
  selectedValue.current = formData;
  changeSelection.current = onChange;
  const [reviewers, setReviewers] = useState<ReviewerDescriptor[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReviewers = useCallback(async () => {
    if (!validEvaluationId) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoaded(false);
    setError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('horizon-assess');
      const response = await readJson(
        await fetchApi.fetch(
          `${baseUrl}/v1/evaluations/${encodeURIComponent(
            evaluationId,
          )}/eligible-reviewers`,
        ),
      );
      if (
        !isReviewersResponse(response) ||
        response.evaluationId !== evaluationId
      ) {
        throw new Error('The reviewer service returned an invalid response');
      }
      if (sequence === requestSequence.current) {
        setReviewers(response.reviewers);
        setLoaded(true);
        const candidate = pendingSelection.current;
        pendingSelection.current = '';
        if (
          candidate &&
          response.reviewers.some(reviewer => reviewer.entityRef === candidate)
        ) {
          changeSelection.current(candidate);
        }
      }
    } catch (caught) {
      if (sequence === requestSequence.current) {
        pendingSelection.current = '';
        setReviewers([]);
        if (selectedValue.current) changeSelection.current('');
        setError(
          caught instanceof Error
            ? caught.message
            : 'Unable to load eligible reviewers',
        );
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [discoveryApi, evaluationId, fetchApi, validEvaluationId]);

  useLayoutEffect(() => {
    if (!initialSelectionHandled.current) {
      initialSelectionHandled.current = true;
      if (formData) onChange('');
      return;
    }
    if (previousEvaluationId.current !== evaluationId) {
      previousEvaluationId.current = evaluationId;
      pendingSelection.current = '';
      if (formData) onChange('');
    }
  }, [evaluationId, formData, onChange]);

  useEffect(() => {
    if (!validEvaluationId) {
      requestSequence.current += 1;
      setReviewers([]);
      setLoaded(false);
      setLoading(false);
      setError('');
      return undefined;
    }
    void fetchReviewers();
    return () => {
      requestSequence.current += 1;
    };
  }, [fetchReviewers, validEvaluationId]);

  useEffect(() => {
    if (!validEvaluationId && formData) onChange('');
  }, [formData, onChange, validEvaluationId]);

  useEffect(() => {
    if (
      loaded &&
      formData &&
      !reviewers.some(reviewer => reviewer.entityRef === formData)
    ) {
      onChange('');
    }
  }, [formData, loaded, onChange, reviewers]);

  const selected = reviewers.find(reviewer => reviewer.entityRef === formData);
  const locked = Boolean(disabled || readonly || loading);

  return (
    <Box>
      <TextField
        id={`${fieldId}-reviewer`}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required={required}
        disabled={
          locked ||
          !validEvaluationId ||
          Boolean(error) ||
          (loaded && reviewers.length === 0)
        }
        label="Assigned reviewer"
        value={selected?.entityRef ?? ''}
        onChange={event => onChange(event.target.value)}
        helperText="Only independent users authorized for this evaluation are listed; you cannot assign yourself."
        margin="normal"
        variant="outlined"
      >
        {reviewers.length > 0 && <option value="">Select one reviewer</option>}
        {reviewers.map(reviewer => (
          <option key={reviewer.entityRef} value={reviewer.entityRef}>
            {reviewer.displayName} — {reviewer.roles.map(roleLabel).join(' / ')}
          </option>
        ))}
      </TextField>

      {!validEvaluationId && (
        <Typography variant="body2" color="textSecondary">
          Enter the assessment evaluation ID first to load its eligible
          reviewers.
        </Typography>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gridGap={8} mt={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading eligible reviewers…</Typography>
        </Box>
      )}

      {error && (
        <Box mt={1}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button color="primary" size="small" onClick={fetchReviewers}>
            Retry
          </Button>
        </Box>
      )}

      {loaded && !loading && !error && reviewers.length === 0 && (
        <Typography color="error" variant="body2">
          No independent reviewer is currently eligible for this evaluation.
          Contact the Horizon administrator.
        </Typography>
      )}

      {rawErrors?.length ? (
        <Typography color="error" variant="caption">
          {rawErrors.join('; ')}
        </Typography>
      ) : null}
    </Box>
  );
}
